import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { type IntegrationsSettings } from "@/lib/onlineBooking";
import { SoldOutNightsEditor } from "@/components/soldOut/SoldOutNightsEditor";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

interface TenantSettings {
  integrations: IntegrationsSettings;
}

type TouristImportSoldOutPanelProps = {
  canWrite: boolean;
};

export function TouristImportSoldOutPanel({ canWrite }: TouristImportSoldOutPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [touristEnabled, setTouristEnabled] = useState(false);
  const [soldOutNights, setSoldOutNights] = useState<string[]>([]);
  const [newSoldOutDate, setNewSoldOutDate] = useState("");
  const [soldOutMessage, setSoldOutMessage] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        const tourist = settings.integrations?.tourist_integration;
        setTouristEnabled(tourist?.enabled ?? false);
        setSoldOutNights(tourist?.sold_out_nights ?? []);
        setSoldOutMessage(tourist?.sold_out_message ?? "");
        setNewSoldOutDate("");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/integrations", {
        tourist_integration_enabled: touristEnabled,
        tourist_sold_out_nights: soldOutNights,
        ...(soldOutMessage.trim() ? { tourist_sold_out_message: soldOutMessage.trim() } : {}),
      });
      setSuccess(true);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function onAddSoldOutDate() {
    if (!newSoldOutDate || soldOutNights.includes(newSoldOutDate)) return;
    setSoldOutNights((prev) => [...prev, newSoldOutDate].sort());
    setNewSoldOutDate("");
  }

  function onRemoveSoldOutDate(iso: string) {
    setSoldOutNights((prev) => prev.filter((d) => d !== iso));
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <form className="space-y-6" onSubmit={onSave}>
      <Card
        title="Tourist-Anbindung"
        description="Ausgebuchte Nächte für die Online-Buchung mit Tourist-PMS steuern."
        bodyClassName="space-y-4"
      >
        <p className="rounded-lg border border-separator/80 bg-fill-quaternary px-3 py-2 text-xs text-label-secondary">
          Freigeschaltete Tablets können diese Einstellungen unter{" "}
          <code className="text-label">/tourist-panel/&lt;code&gt;</code> bearbeiten. Die
          Geräte-Freischaltung erfolgt im Tab „Geräte“.
        </p>
        <fieldset className="space-y-4" disabled={!canWrite}>
          <label className="flex min-w-0 items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={touristEnabled}
              onChange={(e) => setTouristEnabled(e.target.checked)}
            />
            <span>
              Tourist-Anbindung aktiv
              <span className="mt-0.5 block text-xs text-label-secondary">
                Deaktiviert die automatische Verfügbarkeitsprüfung und aktiviert die
                Ausgebucht-Tage sowie die Tourist-E-Mail-Benachrichtigungen.
              </span>
            </span>
          </label>

          <SoldOutNightsEditor
            soldOutNights={soldOutNights}
            soldOutMessage={soldOutMessage}
            newSoldOutDate={newSoldOutDate}
            canWrite={canWrite}
            onSoldOutMessageChange={setSoldOutMessage}
            onNewSoldOutDateChange={setNewSoldOutDate}
            onAddSoldOutDate={onAddSoldOutDate}
            onRemoveSoldOutDate={onRemoveSoldOutDate}
          />
        </fieldset>
      </Card>

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {success ? <p className="text-sm text-status-green">Gespeichert.</p> : null}

      {canWrite ? (
        <Button type="submit" disabled={saving}>
          {saving ? "Speichern…" : "Speichern"}
        </Button>
      ) : null}
    </form>
  );
}
